import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { colors } from '../../../theme/tokens';

const DARK = colors.textPrimary;

export interface DonutStatItem {
  label: string;
  value: number;
  color: string;
}

export interface DonutStatProps {
  items: DonutStatItem[];
  size?: number;
  // Set false when the caller renders extra content (e.g. a stat row) below
  // DonutStat inside the same card — otherwise this component's own
  // height:100% swallows the whole card and pushes that content out of view.
  // The caller should then wrap DonutStat + its extra content together in a
  // shared height:100%/centered flex column instead.
  fillHeight?: boolean;
}

export default function DonutStat({
  items,
  size = 320,
  fillHeight = true,
}: DonutStatProps) {
  const total = items.reduce((s, i) => s + i.value, 0);
  const data = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => i.value),
        backgroundColor: items.map((i) => i.color),
        borderWidth: 0,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 70,
    legend: { display: false },
    tooltips: {
      callbacks: {
        label: (item: any, d: any) => {
          const val = d.datasets[0].data[item.index];
          const pct = total ? Math.round((val / total) * 100) : 0;
          return ` ${d.labels[item.index]}: ${val} (${pct}%)`;
        },
      },
    },
  };

  return (
    <div
      style={
        fillHeight
          ? {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center',
            }
          : {}
      }
    >
      <div
        style={{
          width: '100%',
          maxWidth: size,
          aspectRatio: '1 / 1',
          margin: '0 auto',
        }}
      >
        <Doughnut data={data} options={options} />
      </div>
      <div style={{ marginTop: 20 }}>
        {items.map((it) => {
          const pct = total ? Math.round((it.value / total) * 100) : 0;
          return (
            <div
              key={it.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: it.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.textSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {it.label}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 800, color: DARK }}>
                  {it.value}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: colors.textFaint,
                    minWidth: 32,
                    textAlign: 'right',
                  }}
                >
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
